import { Editor } from './Editor'
import { EditorDataProvider } from './EditorDataProvider'
import { useEffect, useLayoutEffect, useState } from 'react'
import { TCanvas } from './Canvas'
import {
  TEditorConfig,
  IPdfGenerationPayload,
  IProductConfig,
  TDesignSide,
} from './editor.types'

import {
  checkForLegacyColorConfig,
  constructPayload,
  findFileByName,
  generateEditorConfig,
} from './editor.utils'
import {
  ProductVariant,
  TOnlineEditorConfiguration,
} from '@pageFeatures/design/designUpload/types'
import { editorProductsConfig } from '@pageFeatures/design/onlineEditor/editor-products.config'
import { useEditorFullScreenMode } from '../UnifiedEditorProvider'
import isEqual from 'lodash/isEqual'
import { TFileWithState } from '../designUpload/components/UploadDropzone/UploadDropzone'
import {
  useFilesWithState,
  useSelectedFile,
} from '../designUpload/components/UploadDropzone/UploadDropzone.provider'
import { useCanvasConfig } from './Canvas/useCanvasConfig.hook'
import { useScrollPosition } from '@utils/responsiveness/useScrollPosition'

type TAnnounceDirtyFormFn = (dirty: boolean) => void

export function EditorWrapper({
  urlSlug,
  productOption,
  onDesignConfigChange,
  preloadedOnlineEditorConfig,
  readOnlyMode = false,
  announceDirtyForm,
}: {
  urlSlug: string
  productOption: ProductVariant
  onDesignConfigChange: ({
    pdfGenerationPayload,
    onlineEditorOrderPayload,
  }: {
    pdfGenerationPayload: IPdfGenerationPayload
    onlineEditorOrderPayload: TOnlineEditorConfiguration
  }) => void
  preloadedOnlineEditorConfig: TOnlineEditorConfiguration
  readOnlyMode?: boolean
  announceDirtyForm: TAnnounceDirtyFormFn
}) {
  const [hasScrolled, setHasScrolled] = useState(false)

  useScrollPosition(
    ({ prevPos, currPos }) => {
      const scrolled = currPos.y < prevPos.y
      if (scrolled !== hasScrolled) {
        setHasScrolled(scrolled)
      }
    },
    [hasScrolled]
  )

  const { isFullScreen } = useEditorFullScreenMode()
  const { selectedFile, setSelectedFile } = useSelectedFile()
  const productEditorConfig: IProductConfig = editorProductsConfig[urlSlug]
  const [activeDesignSide, setActiveDesignSide] = useState<TDesignSide>('front')
  const { filesWithState } = useFilesWithState()
  const [selectedFilesPerSide, setSelectedFilesPerSide] = useState<
    Record<TDesignSide, TFileWithState>
  >({
    front: null,
    back: null,
  })
  const [initialFormData, setInitialFormData] = useState<{
    canvasConfig: Record<TDesignSide, TCanvas>
  }>(null)

  /** Editor Config */
  const [editorConfig, setEditorConfig] = useState<TEditorConfig>(
    checkForLegacyColorConfig(
      preloadedOnlineEditorConfig?.editorConfig,
      productOption
    ) || {
      ...generateEditorConfig({
        productOption,
        productEditorConfig,
      }),
    }
  )

  useEffect(() => {
    const editorConfig: TEditorConfig = generateEditorConfig({
      productOption,
      productEditorConfig,
    })
    setEditorConfig({ ...editorConfig })
  }, [productOption, productEditorConfig])

  const { canvasConfig, setCanvasConfig } = useCanvasConfig({
    preloadedOnlineEditorConfig,
    editorConfig,
  })

  /** Managing files selection */
  useEffect(() => {
    setSelectedFilesPerSide({
      ...selectedFilesPerSide,
      [activeDesignSide]: selectedFile,
    })
  }, [selectedFile])

  useEffect(() => {
    setSelectedFile(selectedFilesPerSide[activeDesignSide])
  }, [activeDesignSide])

  /** Generating editor output */
  useLayoutEffect(() => {
    const designDataPayload: IPdfGenerationPayload = constructPayload({
      canvasConfig,
      editorConfig,
    })

    const selectedFileNames = {
      front: selectedFilesPerSide?.front?.file?.name,
      back: selectedFilesPerSide?.back?.file?.name,
    }

    onDesignConfigChange({
      pdfGenerationPayload: designDataPayload,
      onlineEditorOrderPayload: {
        editorConfig,
        canvasConfig,
        selectedFileNames,
      },
    })
  }, [canvasConfig, editorConfig, selectedFile?.fileAsSVG])

  useEffect(() => {
    /** For the legacy configuration */
    if (
      preloadedOnlineEditorConfig?.selectedFileName &&
      !selectedFilesPerSide?.front
    ) {
      setSelectedFilesPerSide({
        front: findFileByName(
          filesWithState,
          preloadedOnlineEditorConfig?.selectedFileName
        ),
        back: null,
      })

      setSelectedFile(
        findFileByName(
          filesWithState,
          preloadedOnlineEditorConfig?.selectedFileName
        )
      )
    }

    /** For the updated configuration */
    if (
      preloadedOnlineEditorConfig?.selectedFileNames &&
      !selectedFilesPerSide?.front
    ) {
      setSelectedFilesPerSide({
        front: findFileByName(
          filesWithState,
          preloadedOnlineEditorConfig?.selectedFileNames?.front
        ),
        back: findFileByName(
          filesWithState,
          preloadedOnlineEditorConfig?.selectedFileNames?.back
        ),
      })
      setSelectedFile(
        findFileByName(
          filesWithState,
          preloadedOnlineEditorConfig?.selectedFileNames?.front
        )
      )
    }
  }, [preloadedOnlineEditorConfig?.selectedFileNames, filesWithState])

  /** Announcing changes */
  useEffect(() => {
    announceDirtyForm(!isEqual(canvasConfig, initialFormData?.canvasConfig))
  }, [canvasConfig, initialFormData])

  return (
    <div
      className={`${
        hasScrolled ? 'top-16' : 'top-60'
      } transition-all duration-300 mb-36 ${isFullScreen ? '' : 'sticky'}`}
    >
      <EditorDataProvider
        value={{
          canvasConfig: canvasConfig,
          updateCanvasConfig: setCanvasConfig,
          editorConfig,
          activeDesignSide,
          setActiveDesignSide,
        }}
      >
        <Editor readOnlyMode={readOnlyMode} />
      </EditorDataProvider>
    </div>
  )
}
